import React, { useEffect, useState } from "react";
import type { GetProp, TableProps } from "antd";
import { Table } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import moment from "moment";
import { getCommonState, getFinanceState } from "../../redux/store/getState";
import modifyId from "../../utils/modifyId";
import { Finance } from "../../redux/apiSlice/financeSlice";
import {
  getFinanceAtcFilter,
  getFinanceAtcUser,
} from "../../redux/apiActions/financeAction";
import useSearch from "../../hooks/useSearch";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];
type TablePaginationConfig = Exclude<
  GetProp<TableProps, "pagination">,
  boolean
>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: SorterResult<any>["field"];
  sortOrder?: SorterResult<any>["order"];
  filters?: Parameters<GetProp<TableProps, "onChange">>[1];
}

const FinanceAtcTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { financeAtc, loading } = useAppSelector(getFinanceState);
  const { filterFrom, filterTo } = useAppSelector(getCommonState);
  const [data, setData] = useState<Finance[]>();

  const { user } = useUser();
  useSearch(financeAtc, setData);

  useEffect(() => {
    if (filterFrom && filterTo) {
      dispatch(getFinanceAtcFilter({ from: filterFrom, to: filterTo }));
    } else {
      dispatch(getFinanceAtcUser(user?.atc.id || ""));
    }
  }, [filterFrom, filterTo, dispatch, user]);

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const handleTableChange: TableProps<Finance>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      // setData([]);
    }
  };

  const columns: ColumnsType<Finance> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },

    {
      title: "ATC User",
      dataIndex: "atc_name",
    },
    {
      title: "Exam Name",
      dataIndex: "exam_name",
    },

    {
      title: "Request ID",
      dataIndex: "request_id",
      render: (request_id) => <>{modifyId(request_id)}</>,
    },
    {
      title: "Requested Vouchers",
      dataIndex: "requested_vouchers",
    },
    {
      title: "Used Vouchers",
      dataIndex: "used_vouchers",
    },
    {
      title: "Payment ID",
      dataIndex: "payment_id",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => <>${amount}</>,
    },
    {
      title: "Invoice No.",
      dataIndex: "invoice_number",
    },
  ];

  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      loading={loading.getRequest}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      pagination={tableParams.pagination}
      onChange={handleTableChange}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default FinanceAtcTable;
